import React, { useEffect, useState } from 'react';

import { usePriceRowContext } from '../../../price-row-context';

import CampaignBadge from 'src/components/campaign-badge';
import { ShippingLabel } from './shipping-label';
import { PriceLabel } from './price-label';
import { HomeDeliveryBadge } from './home-delivery-badge';
import { PriceTypeNoticeBadge } from './price-type-badge';
import { RecommendedRetailerBadge } from './recommended-retailer-badge';
import { CountryBadge } from './country-badge';
import { StockBadge } from './stock-badge';
import { DeliveryTime } from '../delivery-time';

import { AlternativePrice } from 'src/types';

import {
  useStockStatusExperiment,
  useAuthorisedDealerExperiment,
} from 'src/hooks/experiments';

import { getCampaignByOfferId } from '../../../utils';
import { getDeliveryDays, ShippingType } from '../delivery-time/utils';

import { Column, DesktopOnly, FirstRow, MobileOnly, SecondRow } from './styles';

interface IStockPriceInfo {
  alternativeOffer?: AlternativePrice;
}

export const StockPriceInfo: React.FC<IStockPriceInfo> = props => {
  const { alternativeOffer } = props;
  const { campaign, offer } = usePriceRowContext();
  const { featured, isInternationalPrice, deliveryOptions, store } = offer;

  // Get props from alternative offer if supplied
  const id = alternativeOffer ? alternativeOffer.id : offer.priceId;
  const price = alternativeOffer ? alternativeOffer.price : offer.price;
  const shipping = alternativeOffer
    ? alternativeOffer.shipping
    : offer.shipping;
  const stock = alternativeOffer ? alternativeOffer.stock : offer.stock;
  const authorizedDealer = alternativeOffer
    ? null // Not applicable for alternative offers
    : offer.authorizedDealer;
  const priceType = alternativeOffer
    ? null // Not applicable for alternative offers
    : offer.priceType;
  const homeDelivery = alternativeOffer
    ? null // Not applicable for alternative offers
    : deliveryOptions &&
      deliveryOptions.find(option => option && option.type === 'HOME_DELIVERY');

  const activeCampaign = getCampaignByOfferId(id, campaign);

  // Experiment on showing Stock Status vs Delivery time (Delivery time is default)
  const [showStockStatus, setShowStockStatus] = useState(false);
  const experimentVariant = useStockStatusExperiment();

  // Experiment on showing Authorised Dealer badge
  const [
    showNewAuthorisedDealerBadge,
    setShowNewAuthorisedDealerBadge,
  ] = useState(false);
  const authorisedDealerExperiment = useAuthorisedDealerExperiment();

  useEffect(() => {
    const hasDeliveryTime =
      shipping && Boolean(getDeliveryDays(ShippingType.CHEAPEST, shipping));
    setShowStockStatus(!hasDeliveryTime && experimentVariant);
    setShowNewAuthorisedDealerBadge(authorisedDealerExperiment);
  }, [authorisedDealerExperiment, experimentVariant, shipping]);

  return (
    <Column>
      <FirstRow>
        {price && featured && <ShippingLabel price={price} />}

        {activeCampaign && (
          <DesktopOnly>
            <CampaignBadge colors={activeCampaign.media.colors} />
          </DesktopOnly>
        )}

        {price && <PriceLabel price={price} activeCampaign={activeCampaign} />}
      </FirstRow>
      {featured && (
        <SecondRow>
          <DesktopOnly>
            {authorizedDealer && !showNewAuthorisedDealerBadge && (
              <RecommendedRetailerBadge />
            )}
            {priceType && <PriceTypeNoticeBadge priceType={priceType} />}
            {store?.countryCode && isInternationalPrice && (
              <CountryBadge countryCode={store?.countryCode} />
            )}
            <StockBadge
              status={stock?.status}
              statusText={stock?.statusText}
              showLabel={showStockStatus}
            />
            {homeDelivery && <HomeDeliveryBadge />}
          </DesktopOnly>

          <MobileOnly>
            {authorizedDealer && !showNewAuthorisedDealerBadge && (
              <RecommendedRetailerBadge showCaptionText={false} />
            )}
            <StockBadge
              status={stock?.status}
              statusText={stock?.statusText}
              showLabel={showStockStatus}
            />
          </MobileOnly>
          {!showStockStatus && (
            <DeliveryTime shipping={shipping} stockStatus={stock?.status} />
          )}
        </SecondRow>
      )}
    </Column>
  );
};
